import { useState } from 'react';

/**
 * POM-6010 — the review session identity.
 *
 * One popup open is one session. Every call the Review Lot & NDC dialog makes carries the same id,
 * so the audit trail can answer "what did this user do in that sitting" as a single query: the
 * session-opened event, and every per-row commit event that followed it. The per-request id cannot
 * do that — each call gets its own — and reconstructing a session from timestamps is guesswork the
 * moment two users review overlapping batches at once.
 *
 * The contract, exactly:
 *   - minted when the dialog opens
 *   - stable for that whole open, so two commits in one sitting share one id
 *   - a new id on the next open
 */

/**
 * Matches ScanSessionContext's generator, including its fallback: `crypto.randomUUID` needs a
 * secure context, and this runs behind a VPN-fronted host where that is not guaranteed. A
 * timestamp-plus-random id is weaker but still unique enough to group one user's sitting, which is
 * all this identifies — it is not a security token.
 */
export const newReviewSessionId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
  `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * The current session id, given whether the dialog is open.
 *
 * Derived during render — React's "adjusting state when a prop changes" pattern — rather than in an
 * effect, because the dialog fires its review query from an effect of its own. Minting in a
 * competing effect would make the first call of every session silently untraceable if the ordering
 * ever changed, and that failure is invisible: the screen works perfectly and only the audit trail
 * is wrong.
 *
 * The `open` transition is handled as well as the mount-time initialiser, deliberately. Today the
 * parent renders this dialog conditionally, so a fresh mount already means a fresh id — but that is
 * the parent's implementation detail. If it ever switches to an always-mounted dialog driven by
 * `open`, sessions would silently bleed into one another without this.
 */
export const useReviewSessionId = (open) => {
  const [sessionId, setSessionId] = useState(newReviewSessionId);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    // Only on the closed -> open edge. Closing keeps the id, so anything still settling as the
    // dialog tears down is still attributed to the session it belonged to.
    if (open) setSessionId(newReviewSessionId());
  }

  return sessionId;
};

export default useReviewSessionId;
